import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import FavoriteCard from "../components/FavoriteCard";
import { useNavigate } from "react-router-dom";
import creator1Img from "../assets/images/creator1.png";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function FavoritedImages() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [favoritedImages, setFavoritedImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = profile?.accessToken;
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchFavorites = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}accounts/favorites/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => []);
        if (!response.ok) {
          throw new Error(data.error || "Failed to load favorited images");
        }
        setFavoritedImages(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error loading favorited images:", error);
        toast.error(error.message || "Failed to load favorited images");
        setFavoritedImages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [profile?.accessToken]);

  const removeFavorite = async (favorite) => {
    const token = profile?.accessToken;
    if (!token || !favorite?.body_part_image_id) return;

    try {
      const response = await fetch(`${API_BASE}accounts/favorites/remove/${favorite.body_part_image_id}/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok && response.status !== 204) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to remove favorite");
      }

      setFavoritedImages((prev) => prev.filter((item) => item.id !== favorite.id));
      toast.success("Removed from favorites");
    } catch (error) {
      console.error("Error removing favorite:", error);
      toast.error(error.message || "Failed to remove favorite");
    }
  };

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="Favorited Images" />
        
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#ceb46a] mb-2">
                Favorited Images
              </h1>
              <p className="text-gray-400">
                {loading ? "Loading..." : `${favoritedImages.length} favorited images`}
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-[#ceb46a] hover:text-[#d6c074] text-sm font-medium transition-colors duration-200 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Images Grid */}
        {!loading && favoritedImages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {favoritedImages.map((creator) => (
              <FavoriteCard
                key={creator.id}
                avatar={creator.image_url || creator1Img}
                isFavorite={true}
                contributorName={creator.contributor_screen_name || creator.contributor_name}
                bodyPart={creator.body_part}
                onToggleFavorite={() => removeFavorite(creator)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-[#1c1c1c] rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {loading ? "Loading Favorited Images" : "No Favorited Images"}
            </h3>
            <p className="text-gray-400 mb-6">
              {loading
                ? "Fetching your saved images..."
                : "You haven't favorited any images yet. Start exploring and favorite the ones you love!"}
            </p>
            {!loading && (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-[#ceb46a] text-black px-6 py-2 rounded-lg font-medium hover:bg-[#d6c074] transition-colors duration-200"
              >
                Explore Images
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
